import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import {experimentalConditionValuesToString} from 'workflows/util/ExperimentalConditionUtil';

import {StageRunGraph, TaggedNodeGraph} from './TaggedNodeGraph';

/**
 * Abstract class representing a graph where the nodes have been 'aggregated' via an aggregation
 * function specified by `getAggregatedNodeId`.
 * Makes the assumption that all nodes within an aggregation have the same tag.
 */
class AggregatedNodeGraph<T extends TaggedNodeGraph> extends TaggedNodeGraph {
  unaggregatedGraph: T;

  constructor(taggedNodeGraph: T) {
    super();
    this.unaggregatedGraph = taggedNodeGraph;

    // Memoized functions
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getAggregatedNodeId = _.memoize(this.getAggregatedNodeId);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getNodeIdsByAggregatedNodeId = _.memoize(this.getNodeIdsByAggregatedNodeId);
  }

  getAggregatedNodeId(nodeId: string): string {
    throw new Error('Must be implemented by a subclass.');
  }

  // Override superclass method
  getNodeIds(): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.uniq(this.unaggregatedGraph.getNodeIds().map((nodeId) => this.getAggregatedNodeId(nodeId)));
  }

  // Override superclass method
  getParentNodeIds(aggregatedNodeId: string): Array<string> {
    return _.chain(this.getNodeIdsByAggregatedNodeId(aggregatedNodeId))
      .map((nodeId) => this.unaggregatedGraph.getParentNodeIds(nodeId))
      .flatten()
      .map((parentId) => this.getAggregatedNodeId(parentId))
      .uniq()
      .value();
  }

  // Override superclass method
  getTag(aggregatedNodeId: string): number {
    return this.unaggregatedGraph.getTag(this.getNodeIdsByAggregatedNodeId(aggregatedNodeId)[0]);
  }

  // Override superclass method
  getTags(): Array<number> {
    return this.unaggregatedGraph.getTags();
  }

  getNodeIdsByAggregatedNodeId(aggregatedNodeId: string): Array<string> {
    return this.unaggregatedGraph
      .getNodeIds()
      .filter((nodeId) => this.getAggregatedNodeId(nodeId) === aggregatedNodeId);
  }
}

export class AggregatedStageRunGraph extends AggregatedNodeGraph<StageRunGraph> {
  constructor(stageRunGraph: StageRunGraph) {
    super(stageRunGraph);

    // Memoized functions
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this._getDefiningObject = _.memoize(this._getDefiningObject);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this._getAggregatedStageRun = _.memoize(this._getAggregatedStageRun);
  }

  // Override superclass method
  getAggregatedNodeId(nodeId: string): string {
    return `agg_${JSON.stringify(this._getDefiningObject(nodeId))}`;
  }

  /**
   * All nodes with the same 'defining object' are aggregated.
   * In this case, the defining object is an array that represents all the aggregated ancestors
   * of the stage runs.
   */
  _getDefiningObject(
    nodeId: string
  ): Array<{stageIndex: number; conditionName: string; status: STAGE_RUN_STATUS}> {
    const primaryAncestorNodeIds = this.unaggregatedGraph.getPrimaryAncestorNodeIds(nodeId);
    return primaryAncestorNodeIds.map((ancestorNodeId) => {
      const stageRun = this.unaggregatedGraph.getStageRun(ancestorNodeId);
      const conditionValues = stageRun.stageFlowNode.experimentalConditionValues;
      return {
        stageIndex: this.unaggregatedGraph.getTag(ancestorNodeId),
        conditionName: experimentalConditionValuesToString(conditionValues),
        status: stageRun.status,
      };
    });
  }

  /**
   * Should return the corresponding defining object, essentially inverting `getAggregatedNodeId`.
   */
  _getAggregatedStageRun(
    aggregatedNodeId: string
  ): Array<{stageIndex: number; conditionName: string; status: STAGE_RUN_STATUS}> {
    assertAndContinue(
      aggregatedNodeId.slice(0, 4) === 'agg_',
      `Invalid aggregated node ID ${aggregatedNodeId}`
    );
    return JSON.parse(aggregatedNodeId.slice(4));
  }

  getAggregatedStageRunStatus(aggregatedNodeId: string): STAGE_RUN_STATUS {
    return this._getAggregatedStageRun(aggregatedNodeId).slice(-1)[0].status;
  }
}
